import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight, User, BookOpen, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getBlogArticles } from '../lib/api';
import Navbar from '../components/Navbar';
import { Footer } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subEmail, setSubEmail] = useState('');
  const [subLoading, setSubLoading] = useState(false);
  const [subSuccess, setSubSuccess] = useState(false);
  const [subError, setSubError] = useState('');

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      const data = await getBlogArticles();
      setArticles(data);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubLoading(true); setSubError('');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: subEmail })
      });
      if (res.ok) { setSubSuccess(true); setSubEmail(''); setTimeout(() => setSubSuccess(false), 4000); }
      else { setSubError(language === 'fr' ? 'Erreur. Réessayez.' : 'Error. Try again.'); }
    } catch (err) { setSubError(language === 'fr' ? 'Erreur de connexion' : 'Connection error'); }
    finally { setSubLoading(false); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-[#0B1521]" data-testid="blog-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[#4A90D9] text-sm font-semibold tracking-wider uppercase">
              {language === 'fr' ? 'Notre blog' : 'Our blog'}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3">
              {language === 'fr' ? 'Articles & Actualités' : 'Articles & News'}
            </h1>
            <p className="text-white/60 mt-4 max-w-2xl mx-auto text-lg">
              {language === 'fr' 
                ? "Conseils, astuces et actualités du monde du développement web et mobile."
                : 'Tips, tricks and news from the world of web and mobile development.'}
            </p>
          </motion.div>

          {/* Newsletter Subscribe */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-8 max-w-md mx-auto">
            {subSuccess ? (
              <div className="flex items-center justify-center gap-2 px-6 py-4 bg-green-500/10 border border-green-500/20 rounded-full">
                <CheckCircle size={18} className="text-green-400" />
                <span className="text-green-400 font-medium text-sm">{language === 'fr' ? 'Abonnement réussi !' : 'Subscribed successfully!'}</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <div className="flex-1 relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
                  <input
                    type="email"
                    required
                    value={subEmail}
                    onChange={(e) => setSubEmail(e.target.value)}
                    placeholder={language === 'fr' ? 'Votre email pour suivre le blog' : 'Your email to follow the blog'}
                    className="w-full pl-10 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 outline-none focus:border-[#4A90D9]/50 text-sm"
                    data-testid="newsletter-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={subLoading}
                  className="px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors text-sm flex items-center gap-2 disabled:opacity-50 flex-shrink-0"
                  data-testid="newsletter-submit"
                >
                  {subLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                  {language === 'fr' ? "S'abonner" : 'Subscribe'}
                </button>
              </form>
            )}
            {subError && <p className="text-red-400 text-xs text-center mt-2">{subError}</p>}
          </motion.div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <BookOpen size={48} className="mx-auto text-white/20 mb-4 animate-pulse" />
              <p className="text-white/40">{language === 'fr' ? 'Chargement...' : 'Loading...'}</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/40">{language === 'fr' ? 'Aucun article pour le moment.' : 'No articles yet.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${article.slug || article.id}`}
                    className="group block bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-[#4A90D9]/40 transition-all duration-300 hover:transform hover:scale-[1.02]"
                    data-testid={`blog-card-${article.id}`}
                  >
                    {/* Image */}
                    {article.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.image_url}
                          alt={language === 'fr' ? article.title_fr : article.title_en}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-white/40 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(article.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {article.author}
                        </span>
                      </div>
                      <h2 className="text-lg font-bold text-white mb-3 group-hover:text-[#4A90D9] transition-colors line-clamp-2">
                        {language === 'fr' ? article.title_fr : article.title_en}
                      </h2>
                      <p className="text-white/60 text-sm leading-relaxed line-clamp-3">
                        {language === 'fr' ? article.summary_fr : article.summary_en}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-[#4A90D9] text-sm font-medium group-hover:gap-3 transition-all">
                        {language === 'fr' ? 'Lire la suite' : 'Read more'}
                        <ArrowRight size={14} />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
